"use client";

import { useEffect, useState } from "react";
import { QrCode, X } from "lucide-react";

// Eén toegangsbewijs: een kaart met de QR, en tik erop voor een groot scherm
// dat de kassa goed kan scannen. Wit, omdat een scanner een donkere code op
// een lichte grond het best leest. Zie server/toegang.ts.

export type ToegangKaartGegevens = {
  wedstrijd: string;
  dag: string;
  voor: string;
  toelichting: string | null;
  plek: string | null;
  svg: string;
  /** Een voorbeeld buiten het seizoen: dat staat er dan bij. */
  voorbeeld?: boolean;
};

export function ToegangKaart({ bewijs }: { bewijs: ToegangKaartGegevens }) {
  const [groot, setGroot] = useState(false);

  useEffect(() => {
    if (!groot) return;
    const sluit = (e: KeyboardEvent) => e.key === "Escape" && setGroot(false);
    window.addEventListener("keydown", sluit);
    return () => window.removeEventListener("keydown", sluit);
  }, [groot]);

  const onder = [bewijs.voor, bewijs.toelichting].filter(Boolean).join(" · ");
  const plek = bewijs.plek ? `Rennerskwartier: ${bewijs.plek}` : null;

  return (
    <>
      <button
        type="button"
        onClick={() => setGroot(true)}
        className="flex w-full items-center gap-4 rounded-xl border border-border bg-card p-3 text-left"
      >
        {/* De code zelf komt uit de qrcode-bibliotheek, op de tekst die TRS ook mailt. */}
        <span
          className="block size-20 shrink-0 overflow-hidden rounded-lg bg-white [&_svg]:size-full"
          aria-hidden
          dangerouslySetInnerHTML={{ __html: bewijs.svg }}
        />
        <span className="min-w-0 flex-1">
          {bewijs.voorbeeld && (
            <span className="mb-0.5 inline-block rounded-full border border-dashed border-border px-2 py-0.5 text-[0.65rem] font-semibold uppercase tracking-wide text-muted-foreground">
              Voorbeeld
            </span>
          )}
          <span className="block truncate font-semibold text-foreground">{bewijs.wedstrijd}</span>
          <span className="block text-sm text-soft-foreground">{bewijs.dag}</span>
          <span className="block truncate text-xs text-muted-foreground">{onder}</span>
          {plek && <span className="block truncate text-xs font-semibold text-primary">{plek}</span>}
        </span>
        <QrCode className="size-5 shrink-0 text-primary" aria-hidden />
      </button>

      {groot && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={`Toegangscode ${bewijs.wedstrijd}`}
          onClick={() => setGroot(false)}
          className="fixed inset-0 z-[60] flex flex-col items-center justify-center gap-5 bg-white px-6 pb-[env(safe-area-inset-bottom)] pt-[env(safe-area-inset-top)]"
        >
          <button
            type="button"
            onClick={() => setGroot(false)}
            className="absolute right-4 top-[calc(1rem+env(safe-area-inset-top))] rounded-full bg-card p-2 text-foreground"
            aria-label="Sluiten"
          >
            <X className="size-5" aria-hidden />
          </button>
          <div className="w-full max-w-sm [&_svg]:h-auto [&_svg]:w-full" dangerouslySetInnerHTML={{ __html: bewijs.svg }} />
          <div className="text-center text-card">
            <p className="font-display text-xl font-black uppercase tracking-tight">{bewijs.wedstrijd}</p>
            <p className="text-sm font-semibold">{bewijs.dag}</p>
            <p className="text-sm">{onder}</p>
            {plek && <p className="mt-1 text-sm font-semibold">{plek}</p>}
            {bewijs.voorbeeld && (
              <p className="mt-2 font-display text-sm font-black uppercase tracking-wide">Voorbeeld · werkt niet bij de kassa</p>
            )}
          </div>
          <p className="text-center text-xs text-card/70">Zet je scherm op z’n helderst. Tik om te sluiten.</p>
        </div>
      )}
    </>
  );
}
